import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAPContext } from "@/lib/gsap-context";

export function useScrollReveal(options = {}) {
  const ref = useRef(null);
  const { reducedMotion } = useGSAPContext();

  const {
    y = 40,
    x = 0,
    scale = 1,
    opacity = 0,
    duration = 0.9,
    ease = "power3.out",
    start = "top 85%",
    end,
    scrub,
    markers,
    stagger,
    children,
    once = true,
    delay = 0,
    rotateX = 0,
    rotateY = 0,
  } = options;

  useEffect(() => {
    const el = ref.current;
    if (reducedMotion || !el) return;

    const targets = children ? el.querySelectorAll(children) : el;

    const from = {
      y,
      x,
      opacity,
      scale,
      rotateX,
      rotateY,
    };

    const to = {
      y: 0,
      x: 0,
      opacity: 1,
      scale: 1,
      rotateX: 0,
      rotateY: 0,
      duration,
      ease,
      delay,
      stagger: stagger || 0,
      scrollTrigger: {
        trigger: el,
        start,
        end,
        scrub,
        markers,
        toggleActions: once ? "play none none none" : "play reverse play reverse",
      },
    };

    const tween = gsap.fromTo(targets, from, to);

    return () => {
      tween.kill();
      ScrollTrigger.getAll().forEach((st) => {
        if (st.trigger === el || (children && el?.contains(st.trigger))) {
          st.kill();
        }
      });
    };
  }, [reducedMotion, children, delay, duration, ease, end, markers, once, opacity, rotateX, rotateY, scale, scrub, stagger, start, x, y]);

  return ref;
}
